// src/components/auth/LoginForm.tsx

import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Alert,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Person as PersonIcon,
    Lock as LockIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

export interface LoginFormRef {
    submit: () => void;
}

const LoginForm = forwardRef<LoginFormRef>((_, ref) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();

    const [formData, setFormData] = useState({
        username: '',
        password: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState<string>('');

    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
    }));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
        if (serverError) {
            setServerError('');
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.username.trim()) {
            newErrors.username = 'Введите номер телефона';
        }

        if (!formData.password) {
            newErrors.password = 'Введите пароль';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        setServerError('');

        try {
            await login({
                username: formData.username.trim(),
                password: formData.password,
            });

            // После логина user обновлён в AuthContext — но нам нужно узнать флаг
            // ВАЖНО: редирект делаем не здесь, а в компоненте через useEffect
            // Пока просто навигация — но AuthContext сам разберётся
            const from = (location.state as any)?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
        } catch (error: any) {
            // Обобщённое сообщение об ошибке
            if (error?.response?.status === 401) {
                setServerError('Неверный номер телефона или пароль');
            } else if (error?.response?.status === 403) {
                setServerError('Доступ запрещён');
            } else if (error?.response?.data?.error) {
                setServerError(error.response.data.error);
            } else {
                setServerError('Произошла ошибка при входе. Попробуйте позже.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    return (
        <Box>
            {serverError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {serverError}
                </Alert>
            )}

            <TextField
                fullWidth
                label="Номер телефона"
                name="username"
                value={formData.username}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                error={!!errors.username}
                helperText={errors.username || "Введите номер в формате +79991234567"}
                disabled={loading}
                autoComplete="username"
                autoFocus
                sx={{ mb: 2 }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <PersonIcon color="action" />
                            </InputAdornment>
                        ),
                    },
                }}
            />

            <TextField
                fullWidth
                label="Пароль"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                error={!!errors.password}
                helperText={errors.password}
                disabled={loading}
                autoComplete="current-password"
                sx={{ mb: 3 }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    size="small"
                                >
                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />

            <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={loading}
                sx={{ mb: 2 }}
            >
                {loading ? 'Вход...' : 'Войти'}
            </Button>
        </Box>
    );
});

LoginForm.displayName = 'LoginForm';

export default LoginForm;